import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { useSeason } from './useSeason';

export interface ScheduleEntry {
  id: string;
  season_year: number;
  week_number: number;
  week_date: string;
  lane_assignment: string;
  team1_id: number | null;
  team2_id: number | null;
  team1_name: string | null;
  team2_name: string | null;
}

export interface Team {
  team_id: number;
  team_number: number;
  team_name: string;
  season_year: number;
}

export function useSchedule() {
  const { selectedSeason } = useSeason();
  const [schedules, setSchedules] = useState<ScheduleEntry[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchScheduleData() {
      if (!selectedSeason) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch schedules for the selected season
        const { data: schedulesData, error: schedulesError } = await supabase
          .from('season_schedules')
          .select('*')
          .eq('season_year', selectedSeason)
          .order('week_number', { ascending: true })
          .order('lane_assignment', { ascending: true });

        if (schedulesError) throw schedulesError;

        // Fetch teams for the selected season
        const { data: teamsData, error: teamsError } = await supabase
          .from('teams')
          .select('*')
          .eq('season_year', selectedSeason)
          .order('team_number', { ascending: true });

        if (teamsError) throw teamsError;

        setSchedules(schedulesData || []);
        setTeams(teamsData || []);
      } catch (err) {
        console.error('Error fetching schedule data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load schedule data');
      } finally {
        setLoading(false);
      }
    }

    fetchScheduleData();
  }, [selectedSeason]);

  return {
    schedules,
    teams,
    loading,
    error,
    selectedSeason
  };
}

export function useScheduleByWeek(weekNumber?: number) {
  const { schedules, teams, loading, error, selectedSeason } = useSchedule();

  const weekSchedules = weekNumber
    ? schedules.filter(s => s.week_number === weekNumber)
    : schedules;

  // Get unique weeks for filtering
  const weeks = Array.from(new Set(schedules.map(s => s.week_number))).sort((a, b) => a - b);

  return {
    schedules: weekSchedules,
    teams,
    weeks,
    loading,
    error,
    selectedSeason
  };
}

export function useScheduleByTeam(teamNumber?: number) {
  const { schedules, teams, loading, error, selectedSeason } = useSchedule();

  const teamSchedules = teamNumber
    ? schedules.filter(
        s =>
          (s.team1_id && teams.find(t => t.team_id === s.team1_id)?.team_number === teamNumber) ||
          (s.team2_id && teams.find(t => t.team_id === s.team2_id)?.team_number === teamNumber)
      )
    : schedules;

  return {
    schedules: teamSchedules,
    teams,
    loading,
    error,
    selectedSeason
  };
}
